import type { TKey } from '@/translations/i18n'
import type {
	RoadmapActivityCategory,
	RoadmapActivityStatus,
	RoadmapOverlay
} from './types'

/**
 * The gantt rows, in the order they are drawn. The API stores only which
 * category an activity belongs to, so the titles and the ordering live here —
 * which is also what lets a category with no activities still render its row.
 */
export const ROADMAP_CATEGORIES: {
	id: RoadmapActivityCategory
	title: string
}[] = [
	{ id: 'luxe', title: 'Luxe' },
	{ id: 'pro_photo', title: 'Pro Photo' },
	{ id: 'new_tooling_infra', title: 'New Tooling / Infra' },
	{ id: 'other', title: 'Other' }
]

/**
 * Bar colours per status, sourced from the theme's semantic hues. The legend
 * paints its swatches from this same `bar` string, so a swatch and the bars it
 * describes cannot drift apart.
 */
export const STATUS_STYLES: Record<
	RoadmapActivityStatus,
	{ bar: string; label: TKey }
> = {
	todo: {
		bar: 'border-neutral-300 bg-neutral-100 text-neutral-700',
		label: 'roadmaps:status_todo'
	},
	in_progress: {
		bar: 'border-warning-300 bg-warning-100 text-warning-700',
		label: 'roadmaps:status_in_progress'
	},
	done: {
		bar: 'border-success-300 bg-success-100 text-success-700',
		label: 'roadmaps:status_done'
	},
	blocked: {
		bar: 'border-danger-300 bg-danger-100 text-danger-700',
		label: 'roadmaps:status_blocked'
	},
	qa: {
		/*
		 * The deck used purple; the theme's semantic set has no purple, so QA
		 * takes blue.
		 *
		 * 200/400 rather than the 100/300 every other status uses: the theme
		 * declares `--color-blue-100` twice, and the winning one (the Airbnb DLS
		 * block) is `rgb(23 92 211 / 0.1)`, translucent. A QA bar built on it
		 * would let the grid lines and the holiday band show through while every
		 * other bar stayed opaque. `blue-200` is declared once and is solid.
		 */
		bar: 'border-blue-400 bg-blue-200 text-blue-700',
		label: 'roadmaps:status_qa'
	}
}

/**
 * Minimum pixels per *working day*: weekends are not on the axis, so a week
 * column is five of these, not seven. Every horizontal measure in the chart
 * derives from this single number, which is what keeps bars glued to their
 * columns.
 *
 * A minimum rather than a fixed value: the chart always stretches to fill its
 * container, so this only kicks in once the timeline is long enough (or the
 * viewport narrow enough) that filling would squash it, at which point the
 * chart scrolls instead.
 */
export const MIN_DAY_WIDTH = 24

/**
 * Horizontal pixels the pointer may travel before a press counts as a pan
 * rather than a click. Bars are clickable and the chart pans by dragging, so
 * the two gestures start identically and are told apart by distance alone.
 */
export const PAN_CLICK_SLOP = 4

/**
 * Height of a single-line bar, and the quantum every taller bar grows by: a
 * label that wraps to two lines makes its bar exactly twice this tall. Keeping
 * heights on a multiple of one number is what lets lanes stack without gaps.
 */
export const LANE_HEIGHT = 28
export const LANE_GAP = 6
/** Vertical breathing room inside a bar, top and bottom combined. */
export const BAR_PADDING_Y = 8
/** Breathing room under the last lane of a category. */
export const CATEGORY_PADDING_Y = 12
/** Width of the sticky category column. */
export const CATEGORY_COLUMN_WIDTH = 132

/**
 * Stacking order inside the chart. Every value stays below the theme's
 * `--z-tooltip` (13): the tooltip portals to the body, so it shares a stacking
 * context with the chart and would otherwise slide under the sticky header and
 * category column.
 */
export const Z_CATEGORY_COLUMN = 2
export const Z_TIMELINE_HEADER = 3

/**
 * The window the 2026 page asks the API for, and draws. Activities are fetched
 * for exactly this range (see queries/use-get-roadmap-activities), so widening
 * it widens the chart and the request together.
 */
export const ROADMAP_2026_WINDOW = {
	dateFrom: '2026-01-01',
	dateTo: '2026-12-31'
} as const

/**
 * Bands drawn behind every row. Not activities and not editable, so they stay
 * here rather than in the API: nothing about a company holiday belongs in a
 * table of work items.
 */
export const ROADMAP_2026_OVERLAYS: RoadmapOverlay[] = [
	{
		id: 'humans-holidays',
		title: 'Humans Holidays',
		dateFrom: '2026-08-10',
		dateTo: '2026-08-21'
	}
]
